import { Button, Flex, HStack, Icon, Stack, Text } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { IconType } from "react-icons";
import { FiCheck, FiEdit3 } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  BidStatus,
  PortalEventContextType,
  useConfirmPortalStandingBidMutation,
  useCreatePortalInitialBidMutation,
  useCreatePortalRoundBidMutation,
  useDeclinePortalNegotiationRoundMutation,
  useGetPortalMyBidsQuery,
  useWithdrawPortalNegotiationRoundDeclineMutation,
  useWithdrawPortalPrivateBidMutation,
  type PortalNegotiationRoundDto,
  type PortalSourcingEventDetailDto,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { useDisplayDateWithTime } from "../../../../common/formatting/useDisplayDateWithTime";
import { useConfirmationModal } from "../../../../common/useConfirmationModal";
import { usePortalEventUrls } from "../../../../common/sourcing-events/portal/usePortalEventUrls";
import { roundAcceptsBids } from "./roundAcceptsBids";
import { isInvitedCompetition } from "../isInvitedCompetition";

type Variant = "compact" | "full";

interface Props {
  event: PortalSourcingEventDetailDto;
  round?: PortalNegotiationRoundDto;
  variant: Variant;
}

export const PrivateBidCta = ({ event, round, variant }: Props) => {
  if (!round) return <InitialPrivateBidCta event={event} variant={variant} />;
  if (!roundAcceptsBids(round)) return null;
  if (!round.isCallerInvited) return null;
  return <RoundPrivateBidCta event={event} round={round} variant={variant} />;
};

const RoundPrivateBidCta = ({
  event,
  round,
  variant,
}: {
  event: PortalSourcingEventDetailDto;
  round: PortalNegotiationRoundDto;
  variant: Variant;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { trigger } = useApiError();
  const portalUrls = usePortalEventUrls();
  const confirmationModal = useConfirmationModal();
  const displayDateWithTime = useDisplayDateWithTime();
  const [createRoundBid, { isLoading }] = useCreatePortalRoundBidMutation();
  const [confirmStanding, { isLoading: isConfirming }] = useConfirmPortalStandingBidMutation();
  const [declineRound, { isLoading: isDeclining }] = useDeclinePortalNegotiationRoundMutation();
  const [withdrawDecline, { isLoading: isWithdrawingDecline }] = useWithdrawPortalNegotiationRoundDeclineMutation();
  const [locallyDeclined, setLocallyDeclined] = useState<boolean | null>(null);
  // A revised tender is the same document as the one it revises, so it opens in the wizard the
  // competition already uses — the private editor for a private event, the tender wizard for a
  // public one.
  const isPrivate = isInvitedCompetition(event.contextType);
  const existingBid = round.myBid;
  const isSubmitted = existingBid?.status === BidStatus.Submitted;
  const hasConfirmed = isSubmitted && existingBid?.confirmsStandingBid === true;
  const canConfirm = !isSubmitted && round.standingBid != null;
  const isDeclined = !isSubmitted && (locallyDeclined ?? round.declinedAt != null);
  const deadline = displayDateWithTime(round.deadline);

  const openBid = (bidId: string) => {
    if (isPrivate) {
      navigate(portalUrls.privateBid.edit.go(event.id, bidId));
    } else {
      navigate(portalUrls.bid.edit.go(event.id, bidId).intro);
    }
  };

  const handleStart = async () => {
    if (existingBid) {
      openBid(existingBid.bidId);
      return;
    }
    try {
      const result = await createRoundBid({ eventId: event.id, roundId: round.id }).unwrap();
      openBid(result.bidId);
    } catch (e) {
      trigger(e);
    }
  };

  const handleConfirm = () => {
    const deliveredAt = round.standingBid?.deliveredAt;
    const discardsStartedBid = existingBid?.status === BidStatus.Draft;
    confirmationModal.show({
      title: t("Confirm your current bid?"),
      description: (
        <Stack spacing="2">
          <Text>
            {deliveredAt
              ? t(
                  "Your bid delivered {{time}} is delivered unchanged in this round. You can still change it until the deadline.",
                  { time: displayDateWithTime(deliveredAt) },
                )
              : t(
                  "Your current bid is delivered unchanged in this round. You can still change it until the deadline.",
                )}
          </Text>
          {discardsStartedBid && (
            <Text fontWeight="semibold">{t("The new bid you have started is discarded.")}</Text>
          )}
        </Stack>
      ),
      confirmButtonText: t("Confirm current bid") ?? "Confirm current bid",
      confirmButtonColor: discardsStartedBid ? "red" : "teal",
      onConfirmed: async () => {
        try {
          await confirmStanding({ eventId: event.id, roundId: round.id }).unwrap();
        } catch (e) {
          trigger(e);
        }
      },
    });
  };

  const handleDecline = async () => {
    try {
      await declineRound({ eventId: event.id, roundId: round.id }).unwrap();
      setLocallyDeclined(true);
    } catch (e) {
      trigger(e);
    }
  };

  const handleWithdrawDecline = async () => {
    try {
      await withdrawDecline({ eventId: event.id, roundId: round.id }).unwrap();
      setLocallyDeclined(false);
    } catch (e) {
      trigger(e);
    }
  };

  if (isDeclined) {
    return (
      <CtaLayout
        variant={variant}
        title={t("Round declined")}
        subtitle={
          round.missingLeavesCompetition === false
            ? t("You have declined this round. Your latest bid still stands in the competition.")
            : t("You have declined this round. The buyer can see that you have withdrawn.")
        }
        button={t("Participate anyway")}
        onStart={handleWithdrawDecline}
        isLoading={isWithdrawingDecline}
        isDeclined
      />
    );
  }

  const subtitle = hasConfirmed
    ? t("You have confirmed your current bid. You can review it or change it until the deadline.")
    : isSubmitted
      ? t("Your bid has been submitted. You can review it until the deadline.")
      : round.missingLeavesCompetition === false
        ? t(
            "Deliver a new bid, or confirm that your current bid stands. If you do not answer by {{deadline}}, your latest bid still stands in the competition.",
            { deadline },
          )
        : canConfirm
          ? t(
              "Deliver a new bid, or confirm that your current bid stands. If you do not answer by {{deadline}}, you may not be invited to later rounds.",
              { deadline },
            )
          : t("Deliver a new bid. If you do not answer by {{deadline}}, you may not be invited to later rounds.", { deadline });

  return (
    <>
      {confirmationModal.modal}
      <CtaLayout
        variant={variant}
        title={t("Answer this round")}
        subtitle={subtitle}
        button={isSubmitted ? t("View bid") : existingBid ? t("Continue new bid") : t("Deliver new bid")}
        onStart={handleStart}
        isLoading={isLoading}
        secondaryButton={canConfirm ? t("Confirm current bid") ?? "Confirm current bid" : undefined}
        onSecondary={handleConfirm}
        isSecondaryLoading={isConfirming}
        secondaryIcon={FiCheck}
        declineButton={isSubmitted ? undefined : { onClick: handleDecline, isLoading: isDeclining }}
      />
    </>
  );
};

const InitialPrivateBidCta = ({ event, variant }: { event: PortalSourcingEventDetailDto; variant: Variant }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const portalUrls = usePortalEventUrls();
  const { trigger } = useApiError();
  const confirmationModal = useConfirmationModal();
  const [createInitialBid, { isLoading }] = useCreatePortalInitialBidMutation();
  const [withdraw, { isLoading: isWithdrawing }] = useWithdrawPortalPrivateBidMutation();
  const { data: myBids, isLoading: isLoadingMyBids } = useGetPortalMyBidsQuery({});
  const existingBid = myBids?.bids.find(
    (bid) =>
      bid.eventId === event.id &&
      (bid.status === BidStatus.Draft ||
        bid.status === BidStatus.Submitted ||
        bid.status === BidStatus.Rejected),
  );
  const isSubmitted = existingBid?.status === BidStatus.Submitted;
  const isRejected = existingBid?.status === BidStatus.Rejected;

  const refused = event.frameworkAgreementNoLongerActive === true;

  if (isLoadingMyBids) return null;
  if (refused && !isSubmitted && !isRejected) return null;

  const handleStart = async () => {
    try {
      if (existingBid) {
        navigate(portalUrls.privateBid.edit.go(event.id, existingBid.bidId));
      } else {
        const result = await createInitialBid({ eventId: event.id }).unwrap();
        navigate(portalUrls.privateBid.edit.go(event.id, result.bidId));
      }
    } catch (e) {
      trigger(e);
    }
  };

  const handleEdit = () => {
    if (!existingBid) return;
    confirmationModal.show({
      title: t("Edit bid?"),
      description: t(
        "Your submitted bid is withdrawn while you edit, and no longer counts as delivered until you submit it again.",
      ),
      confirmButtonText: t("Edit bid") ?? "Edit bid",
      onConfirmed: async () => {
        try {
          await withdraw({ eventId: event.id, bidId: existingBid.bidId }).unwrap();
          navigate(portalUrls.privateBid.edit.go(event.id, existingBid.bidId));
        } catch (e) {
          trigger(e);
        }
      },
    });
  };

  return (
    <>
      {confirmationModal.modal}
      <CtaLayout
        variant={variant}
        title={
          isRejected ? t("Your bid was rejected") : refused ? t("Your bid") : t("Submit a bid for this procurement")
        }
        subtitle={
          isRejected
            ? t("Open the bid to read why it was rejected and what you can do.")
            : isSubmitted && refused
              ? t("Your bid has been submitted. You can still open it.")
              : isSubmitted
              ? t("Your bid has been submitted. You can review it until the deadline.")
              : t("Create a draft and fill in price, documents and cover letter before submitting.")
        }
        button={isSubmitted || isRejected ? t("View bid") : existingBid ? t("Continue bid") : t("Start bid")}
        onStart={handleStart}
        isLoading={isLoading}
        secondaryButton={isSubmitted && !refused ? t("Edit bid") ?? "Edit bid" : undefined}
        onSecondary={handleEdit}
        isSecondaryLoading={isWithdrawing}
      />
    </>
  );
};

const CtaLayout = ({
  variant,
  title,
  subtitle,
  button,
  onStart,
  isLoading,
  secondaryButton,
  onSecondary,
  isSecondaryLoading,
  secondaryIcon = FiEdit3,
  declineButton,
  isDeclined = false,
}: {
  variant: Variant;
  title: string;
  subtitle: string;
  button: string;
  onStart: () => void;
  isLoading: boolean;
  secondaryButton?: string;
  onSecondary?: () => void;
  isSecondaryLoading?: boolean;
  secondaryIcon?: IconType;
  declineButton?: { onClick: () => void; isLoading: boolean };
  isDeclined?: boolean;
}) => {
  const primaryStyle = isDeclined
    ? { variant: "outline", colorScheme: "gray", bg: "smBackground" }
    : { variant: "solid", colorScheme: "teal" };
  const { t } = useTranslation();
  const decline = declineButton && (
    <Button
      variant="outline"
      colorScheme="gray"
      size="sm"
      bg="smBackground"
      onClick={declineButton.onClick}
      isLoading={declineButton.isLoading}
    >
      {t("Decline")}
    </Button>
  );
  if (variant === "compact") {
    return (
      <Flex gap="3" rowGap="2" flexWrap="wrap" minW={0}>
        {decline}
        {secondaryButton && onSecondary && (
          <Button
            variant="outline"
            colorScheme="teal"
            size="sm"
            bg="smBackground"
            leftIcon={<Icon as={secondaryIcon} />}
            onClick={onSecondary}
            isLoading={isSecondaryLoading}
          >
            {secondaryButton}
          </Button>
        )}
        <Button
          {...primaryStyle}
          size="sm"
          leftIcon={isDeclined ? undefined : <Icon as={FiEdit3} />}
          onClick={onStart}
          isLoading={isLoading}
          flexShrink={0}
        >
          {button}
        </Button>
      </Flex>
    );
  }

  return (
    <HStack
      spacing="0"
      columnGap="4"
      align="center"
      justify="space-between"
      flexWrap="wrap"
      rowGap="3"
      borderWidth="1px"
      borderColor={isDeclined ? "smBorder" : "smPrimary"}
      bg={isDeclined ? "smBackground" : "smPrimarySofter"}
      rounded="lg"
      px="4"
      py="3"
    >
      <Stack spacing="0">
        <Text fontWeight="semibold" fontSize="sm" color={isDeclined ? "smMuted" : undefined}>
          {title}
        </Text>
        <Text fontSize="sm" color="smMuted">
          {subtitle}
        </Text>
      </Stack>
      <Flex gap="3" rowGap="2" flexWrap="wrap" minW={0} data-testid="round-answer-buttons">
        {decline}
        {secondaryButton && onSecondary && (
          <Button
            variant="outline"
            colorScheme="teal"
            size="sm"
            bg="smBackground"
            leftIcon={<Icon as={secondaryIcon} />}
            onClick={onSecondary}
            isLoading={isSecondaryLoading}
          >
            {secondaryButton}
          </Button>
        )}
        <Button {...primaryStyle} size="sm" onClick={onStart} isLoading={isLoading}>
          {button}
        </Button>
      </Flex>
    </HStack>
  );
};
